import { openDB, IDBPDatabase } from "idb";
import { useEffect, useState, useSyncExternalStore } from "react";
import { isSupplierFinancialEndpoint, purgeSupplierFinancialCache } from "./supplier-financial-cache-policy";
import {isMaintenanceEndpoint} from "./maintenance-offline-policy";
import { authenticatedCacheScope } from "./auth-cache-scope";

const DB_NAME = "infrastock-offline";
const DB_VERSION = 2;
const STORE_CACHE = "cached_docs";
const STORE_QUEUE = "mutation_queue";

/** Personal templates cannot use a URL-keyed, cross-session offline cache or
 * replay under whichever user happens to be signed in when connectivity returns. */
export function isPersonalWasteTemplateEndpoint(endpoint: string): boolean {
  const path = endpoint.split("?")[0].replace(/^\/api(?=\/)/, "").replace(/\/+$/, "");
  return /^\/waste\/templates(?:\/|$)/.test(path);
}

export async function purgePersonalWasteTemplateStorage(db: IDBPDatabase): Promise<void> {
  for (const store of [STORE_CACHE, STORE_QUEUE] as const) {
    const transaction = db.transaction(store, "readwrite");
    let cursor = await transaction.store.openCursor();
    while (cursor) {
      const endpoint = store === STORE_CACHE ? String(cursor.key) : String((cursor.value as QueueItem).endpoint);
      if (isPersonalWasteTemplateEndpoint(endpoint)||isMaintenanceEndpoint(endpoint)) await cursor.delete();
      cursor = await cursor.continue();
    }
    await transaction.done;
  }
}

export type QueueStatus = "queued" | "syncing" | "retry" | "conflict" | "error";

export interface QueueItem {
  id?: number;
  endpoint: string;
  method: string;
  body?: unknown;
  workspaceId?: string;
  createdAt: number;
  status: QueueStatus;
  attempts: number;
  lastError?: string | null;
  lastTriedAt?: number | null;
  label?: string;
}

export interface CacheEntry {
  key: string;
  data: unknown;
  cachedAt: number;
}

let dbPromise: Promise<IDBPDatabase> | null = null;
function getDB(): Promise<IDBPDatabase> {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error("IndexedDB not available"));
  }
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, _newVersion, transaction) {
        if (!db.objectStoreNames.contains(STORE_CACHE)) {
          db.createObjectStore(STORE_CACHE, { keyPath: "key" });
        }
        if (!db.objectStoreNames.contains(STORE_QUEUE)) {
          const store = db.createObjectStore(STORE_QUEUE, {
            keyPath: "id",
            autoIncrement: true,
          });
          store.createIndex("status", "status");
          store.createIndex("createdAt", "createdAt");
        }
        // Version 1 keyed GET snapshots by URL alone. They cannot be attributed
        // safely to a user, so discard snapshots only; keep queued submissions.
        if (oldVersion < 2) void transaction.objectStore(STORE_CACHE).clear();
      },
    }).then(async (db) => {
      await purgeSupplierFinancialCache(db);
      await purgePersonalWasteTemplateStorage(db);
      return db;
    });
  }
  return dbPromise;
}

// Clean historical financial responses even if the current session never
// opens an offline-enabled screen. Future writes/reads also fail closed below.
if (typeof indexedDB !== "undefined") void getDB().catch(() => {});

// ---------- pub/sub for offline state + queue size ----------
type Listener = () => void;
const listeners = new Set<Listener>();
function emit() {
  for (const l of listeners) {
    try {
      l();
    } catch {}
  }
}
function subscribe(l: Listener) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

// ---------- online state ----------
function getInitialOnline(): boolean {
  if (typeof navigator === "undefined") return true;
  return navigator.onLine !== false;
}
let _online = getInitialOnline();
export function isOnline(): boolean {
  return _online;
}
function setOnline(v: boolean) {
  if (_online === v) return;
  _online = v;
  emit();
  if (v) {
    // Best-effort drain when we come back online.
    void drainQueue();
  }
}
if (typeof window !== "undefined") {
  window.addEventListener("online", () => setOnline(true));
  window.addEventListener("offline", () => setOnline(false));
}

export function useOnlineStatus(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => _online,
    () => true,
  );
}

// ---------- queue size cache (for badge) ----------
let _queueCount = 0;
export function getPendingCount(): number {
  return _queueCount;
}
async function refreshQueueCount() {
  try {
    const db = await getDB();
    _queueCount = await db.count(STORE_QUEUE);
    emit();
  } catch {}
}
export function usePendingCount(): number {
  useEffect(() => {
    void refreshQueueCount();
  }, []);
  return useSyncExternalStore(
    subscribe,
    () => _queueCount,
    () => 0,
  );
}

// ---------- document cache (GETs) ----------
function cacheKey(endpoint: string, scope = authenticatedCacheScope()): string {
  return JSON.stringify([scope, endpoint]);
}

export async function clearOfflineDocumentCache(): Promise<void> {
  if (typeof indexedDB === "undefined") return;
  const db = await getDB();
  await db.clear(STORE_CACHE);
}

export function useCachedAt(endpoint: string): number | null {
  const [ts, setTs] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const entry = await readCachedResponse(endpoint);
      if (!cancelled) setTs(entry?.cachedAt ?? null);
    };
    void load();
    const t = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [endpoint]);
  return ts;
}

export async function readCachedResponse(endpoint: string, scope = authenticatedCacheScope()): Promise<CacheEntry | undefined> {
  if (isSupplierFinancialEndpoint(endpoint) || isPersonalWasteTemplateEndpoint(endpoint)||isMaintenanceEndpoint(endpoint)) return undefined;
  if (scope !== authenticatedCacheScope()) return undefined;
  try {
    const db = await getDB();
    if (scope !== authenticatedCacheScope()) return undefined;
    return (await db.get(STORE_CACHE, cacheKey(endpoint,scope))) as CacheEntry | undefined;
  } catch {
    return undefined;
  }
}

export async function writeCachedResponse(endpoint: string, data: unknown, scope = authenticatedCacheScope()): Promise<void> {
  if (isSupplierFinancialEndpoint(endpoint) || isPersonalWasteTemplateEndpoint(endpoint)||isMaintenanceEndpoint(endpoint)) return;
  if (scope !== authenticatedCacheScope()) return;
  try {
    const db = await getDB();
    if (scope !== authenticatedCacheScope()) return;
    await db.put(STORE_CACHE, { key: cacheKey(endpoint,scope), data, cachedAt: Date.now() });
  } catch {}
}

// ---------- mutation queue ----------
export async function enqueueMutation(item: Omit<QueueItem, "id" | "createdAt" | "status" | "attempts">): Promise<number> {
  if(isMaintenanceEndpoint(item.endpoint))throw new Error("Maintenance requires an explicit online submission");
  if (isPersonalWasteTemplateEndpoint(item.endpoint)) throw new Error("Waste templates require an online connection. Please reconnect and try again.");
  const db = await getDB();
  const full: QueueItem = {
    ...item,
    createdAt: Date.now(),
    status: "queued",
    attempts: 0,
    lastError: null,
    lastTriedAt: null,
  };
  const id = (await db.add(STORE_QUEUE, full)) as number;
  void refreshQueueCount();
  return id;
}

export async function listQueue(): Promise<QueueItem[]> {
  try {
    const db = await getDB();
    const items = (await db.getAll(STORE_QUEUE)) as QueueItem[];
    return items.sort((a, b) => a.createdAt - b.createdAt);
  } catch {
    return [];
  }
}

export async function discardQueueItem(id: number): Promise<void> {
  const db = await getDB();
  await db.delete(STORE_QUEUE, id);
  void refreshQueueCount();
}

export async function updateQueueItem(id: number, patch: Partial<QueueItem>): Promise<void> {
  const db = await getDB();
  const existing = (await db.get(STORE_QUEUE, id)) as QueueItem | undefined;
  if (!existing) return;
  await db.put(STORE_QUEUE, { ...existing, ...patch, id });
  void refreshQueueCount();
}

// Sender callback registered by api.ts to actually replay a mutation.
type ReplayFn = (item: QueueItem) => Promise<{ ok: true } | { ok: false; status?: number; message: string }>;
let replayer: ReplayFn | null = null;
export function registerReplayer(fn: ReplayFn) {
  replayer = fn;
}

let draining = false;
export async function drainQueue(): Promise<{ synced: number; failed: number }> {
  if (draining) return { synced: 0, failed: 0 };
  if (!isOnline()) return { synced: 0, failed: 0 };
  if (!replayer) return { synced: 0, failed: 0 };
  draining = true;
  let synced = 0;
  let failed = 0;
  try {
    const items = await listQueue();
    for (const item of items) {
      // Older app versions may have queued personal template writes. Never
      // deliver those under a different session, including manual retries.
      if (isPersonalWasteTemplateEndpoint(item.endpoint)||isMaintenanceEndpoint(item.endpoint)) {
        if (item.id) await discardQueueItem(item.id);
        continue;
      }
      if (item.status === "conflict") continue;
      if (!item.id) continue;
      await updateQueueItem(item.id, { status: "syncing" });
      const res = await replayer(item);
      if (res.ok) {
        await discardQueueItem(item.id);
        synced++;
      } else {
        failed++;
        const status = (res as any).status as number | undefined;
        const isConflict = status === 409 || status === 412 || status === 422;
        await updateQueueItem(item.id, {
          status: isConflict ? "conflict" : "retry",
          attempts: (item.attempts || 0) + 1,
          lastError: res.message,
          lastTriedAt: Date.now(),
        });
        // Stop draining on a non-conflict failure so user sees it and we
        // don't hammer a flaky network.
        if (!isConflict) break;
      }
    }
  } finally {
    draining = false;
    void refreshQueueCount();
  }
  return { synced, failed };
}

export async function retryQueueItem(id: number): Promise<void> {
  const item = (await getDB().then(db => db.get(STORE_QUEUE, id))) as QueueItem | undefined;
  if (item && isPersonalWasteTemplateEndpoint(item.endpoint)) {
    await discardQueueItem(id);
    return;
  }
  await updateQueueItem(id, { status: "queued", lastError: null });
  await drainQueue();
}

// Build a short human label for a queued endpoint when callers don't
// supply one. Best-effort, keeps the sync page readable.
export function describeEndpoint(endpoint: string, method: string): string {
  const path = endpoint.split("?")[0];
  const parts = path.split("/").filter(Boolean);
  // /stocktakes/:id/items/:itemId/count
  if (parts[0] === "stocktakes" && parts[2] === "items" && parts[4] === "count") {
    return "Stocktake count";
  }
  if (parts[0] === "orders" && parts[2] === "receive") return "Delivery receipt";
  if (parts[0] === "orders" && parts[2] === "accept") return "Order accepted";
  if (parts[0] === "orders" && parts[2] === "reject") return "Order rejected";
  if (parts[0] === "orders" && parts[2] === "propose-changes") return "Order changes proposed";
  if (parts[0] === "transfers" && parts[2] === "ship") return "Transfer shipped";
  if (parts[0] === "transfers" && parts[2] === "receive") return "Transfer received";
  return `${method} ${path}`;
}
